import { useCurrency } from "@shared/selectors/globalSelectors";
import { Card, CardContent, CardHeader } from "@shared/ui/card";
import { Separator } from "@shared/ui/separator";
import {
  Banknote,
  Droplet,
  Link2,
  ReceiptText,
  ShieldCheck,
  Zap,
} from "lucide-react";
import React from "react";

interface MeterReading {
  id: number;
  meterNumber: string;
  deviceNumber: string;
  finalReading: string;
  readingDate: string;
  productCode: string;
}

interface SettlementViewProps {
  request: {
    actionTaken: string;
    fieldTechnician: string;
    disconnectionDate: string;
    disconnectionTime: string;
    meterReadings: MeterReading[];
    checklist: string[];
  };
}

interface Checklist {
  finalBillGenerated: boolean;
  finalPaymentReceived: boolean;
  equipmentRetrieved: boolean;
  customerNotified: boolean;
  recordsArchived: boolean;
  accountClosed: boolean;
}

const getMeterIcon = (productCode: string) => {
  const code = productCode.toLowerCase();
  if (code.includes("water")) {
    return <Droplet className="h-5 w-5 text-cyan-600" />;
  } else if (code.includes("electric")) {
    return <Zap className="h-5 w-5 text-blue-600" />;
  }
  return <Zap className="h-5 w-5 text-gray-600" />;
};

const getMeterUnit = (productCode: string) => {
  const code = productCode.toLowerCase();
  if (code.includes("water")) return "gallons";
  if (code.includes("electric")) return "kWh";
  return "";
};

const SettlementView: React.FC<any> = ({ request }) => {
  const currency = useCurrency();
  const isSecurityDepositIncludedInLastBill =
    request?.additionalData?.refundData?.includeServiceCharge;

  const securityDepositAmount =
    request?.additionalData?.refundData?.serviceChargeAmount;

  const totalCreditAmount = request?.additionalData?.refundData?.creditAmount;

  const showSecurityDepositRefund = !isSecurityDepositIncludedInLastBill;

  const showCreditRefund = !request?.additionalData?.refundData?.includeCredit;

  const totalRefundAmount =
    (showSecurityDepositRefund ? securityDepositAmount : 0) +
    (totalCreditAmount > 0 ? totalCreditAmount : 0);
  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="font-bold text-xl flex items-center gap-2">
            <Banknote className="h-5 w-5" /> Refund Settlement
          </div>
        </CardHeader>

        <CardContent className="space-y-4">
          {/* Security Deposit Refund */}
          {showSecurityDepositRefund && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Security Deposit Refund
              </span>
              <span className="font-medium">
                {currency} {securityDepositAmount}
              </span>
            </div>
          )}

          {/* Credit Refund */}
          {showCreditRefund && (
            <>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">
                  Total Credit Amount
                </span>
                <span className="font-medium">
                  {currency} {totalCreditAmount}
                </span>
              </div>
            </>
          )}

          <Separator />

          {/* Total Refund */}
          <div className="flex justify-between text-sm">
            <span className="font-semibold">Total Refund Amount</span>
            <span className="font-semibold">
              {currency} {totalRefundAmount}
            </span>
          </div>

          <Separator />

          {/* Confirm Refund */}
          Amount Refunded: {request?.additionalData?.refundData?.confirmTotalRefund ? 'Yes' : 'No'}
        </CardContent>
      </Card>
      {/* Service Disconnection Details */}
      <div className="bg-white rounded-lg border shadow p-6 space-y-4">
        <div className="font-bold text-lg flex items-center gap-2 text-gray-700">
          <Link2 className="h-5 w-5" />
          Service Disconnection Details
        </div>

        <div className="bg-blue-50 rounded-md p-4 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-blue-700 font-semibold">
              Meter Action Completed
            </div>
            <div className="text-sm mt-2">
              <strong>Action Taken:</strong> '
              {request?.additionalData?.actionTaken ?? "NA"}'
            </div>
            <div className="text-sm">
              <strong>Field Technician:</strong>{" "}
              {request?.additionalData?.fieldTechnician ?? " NA"}
            </div>
          </div>
          <div className="self-end md:self-auto">
            <div className="text-sm">
              <strong>Disconnection Date:</strong> {request?.closeDate ?? "NA"}
            </div>
            <div className="text-sm">
              <strong>Completion Time:</strong>{" "}
              {request?.resolutionTimeAchieved ?? "NA"}
            </div>
          </div>
        </div>
      </div>

      {/* Final Meter Readings */}
      <div className="bg-white rounded-lg border shadow p-6 space-y-4">
        <div className="font-bold text-lg text-gray-700">
          Final Meter Readings
        </div>

        {request?.additionalData?.readingData?.map((meter) => (
          <div
            className={`rounded p-4 ${
              meter.productCode.toLowerCase().includes("water")
                ? "bg-cyan-50"
                : "bg-blue-50"
            }`}
          >
            <div className="font-semibold text-base flex items-center gap-2 mb-2">
              {getMeterIcon(meter.productCode)} {meter.productCode} Meter
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <div>
                <strong>Meter Number:</strong> {meter.meterNumber}
              </div>
              <div>
                <strong>Device Number:</strong> {meter.deviceNumber}
              </div>
              <div>
                <strong>Final Reading:</strong> {meter.meterReading}{" "}
                {getMeterUnit(meter.productCode)}
              </div>
              <div>
                <strong>Reading Date:</strong> {meter.readingDate}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Account Closure Checklist */}
      <div className="bg-white rounded-lg border shadow p-6 space-y-4">
        <div className="font-bold text-lg text-gray-700">
          Account Closure Checklist - Completed
        </div>

        <div className="bg-white rounded-lg border shadow p-6 space-y-4">
          <div className="font-bold text-lg text-gray-700">
            Account Closure Checklist
          </div>
          <div className="bg-green-50 rounded p-4 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isFinalBillGenerateAndSend}
              />
              Final bill generated and sent
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isPaymentReceived}
              />
              Final payment received
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isEquipmentReceived}
              />
              Equipment retrieved
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isCustomerNotified}
              />
              Customer notified of closure
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isRecordsArchieved}
              />
              Records archived
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                disabled
                checked={request?.additionalData?.isAccClosed}
              />
              Account officially closed
            </label>
          </div>
        </div>
      </div>
      <div className="space-y-6">
        {/* Financial Summary Cards */}
        <div className="bg-white border rounded-lg shadow p-6 space-y-4">
          <h2 className="font-bold text-lg text-gray-700">
            Financial Settlement
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Outstanding Balance */}
            <div className="bg-red-50 rounded p-4">
              <div className="text-red-700 font-semibold text-base">
                Outstanding Balance
              </div>
              <div className="text-sm mt-2">
                <div>Total Outstanding</div>
                <div className="font-bold text-red-600">
                  ${request?.additionalData?.totalOutstanding}
                </div>
                <div className="mt-2">Due Date</div>
                <div>{request?.additionalData?.dueDate}</div>
              </div>
            </div>

            {/* Payment History */}
            <div className="bg-blue-50 rounded p-4">
              <div className="text-blue-700 font-semibold text-base">
                Payment History
              </div>
              <div className="text-sm mt-2">
                <div>Last Payment</div>
                <div className="font-bold text-black">
                  ${request?.additionalData?.totalPaymentReceived}
                </div>
                <div className="mt-2">Payment Date</div>
                <div>{request?.additionalData?.lastPayDate ?? "NA"}</div>
              </div>
            </div>

            {/* Security Deposit */}
            <div className="bg-green-50 rounded p-4">
              <div className="text-green-700 font-semibold text-base flex items-center gap-1">
                <ShieldCheck className="w-4 h-4" />
                Security Deposit
              </div>
              <div className="text-sm mt-2">
                <div>Deposit Amount</div>
                <div className="font-bold text-green-700">
                  ${request?.depositAmount}
                </div>
                <div className="mt-2">Deposit Date</div>
                <div>{request?.depositDate}</div>
              </div>
            </div>

            {/* Net Settlement */}
            <div className="bg-yellow-50 rounded p-4">
              <div className="text-yellow-700 font-semibold text-base">
                Net Settlement
              </div>
              <div className="text-sm mt-2">
                <div>Outstanding Amount</div>
                <div className="text-red-600 font-semibold">
                  ${request?.additionalData?.totalOutstanding}
                </div>
                <div className="mt-2 font-bold text-red-600">Net Amount</div>
                <div className="text-xl font-bold text-red-600">
                  ${request?.additionalData?.netAmountDue}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Final Bill Details */}
        <div className="bg-white border rounded-lg shadow p-6 space-y-4">
          <div className="font-bold text-lg text-gray-700 flex items-center gap-2">
            <ReceiptText className="w-5 h-5" />
            Final Bill Details
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
            <div>
              <strong>Final Bill Number:</strong>{" "}
              {request?.additionalData?.billNumber}
            </div>
            <div>
              <strong>Bill Generation Date:</strong>{" "}
              {request?.additionalData?.createdDate}
            </div>
            <div>
              <strong>Final Amount:</strong>{" "}
              <span className="font-bold">
                ${request?.additionalData?.totalOutstanding}
              </span>
            </div>
            <div>
              <strong>Security Deposit Applied:</strong>{" "}
              <span className="text-green-700 font-bold">
                ${request?.additionalData?.securityDeposit}
              </span>
            </div>
            <div>
              <strong>Payment Date:</strong>{" "}
              {request?.additionalData?.createdDate ?? "NA"}
            </div>
            <div>
              <strong>Bill Status:</strong>{" "}
              <span className="text-green-700 font-semibold">
                {request?.billStatus ?? "NA"}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettlementView;
